import { Alert, CircularProgress, Stack } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useMemo, useState, type ComponentProps } from 'react';
import toast from 'react-hot-toast';
import { listSilobagsService, type SilobagListItem } from '@siloreal:shared:api/service/silobag/silobag-list';
import * as HOOKS from '@siloreal:shared:hooks';
import * as UI from '@siloreal:shared:ui';

type LegacyExtractionActionComponent =
  (typeof import('@old/features/silobags/silobag-detail/exctraction-modal'))['ExtractionAction'];

const LegacyExtractionAction = lazy(async () => {
  const module = await import('@old/features/silobags/silobag-detail/exctraction-modal');

  return { default: module.ExtractionAction };
});

const SILOBAG_LIST_QUERY_KEY = ['silopro-vrr-list'] as const;
const MAX_EXTRACTION_SILOBAGS = 2;

type Props = {
  open: boolean;
  silobagIds: readonly string[];
  onClose: () => void;
};

type LegacySelectedSilobags = NonNullable<
  ComponentProps<LegacyExtractionActionComponent>['selectedSilobags']
>;

function normalizeIds(ids: readonly string[]): string[] {
  return [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
}

function orderSilobagsBySelection(
  selectedIds: readonly string[],
  silobags: readonly SilobagListItem[]
): SilobagListItem[] {
  const silobagById = new Map(silobags.map((silobag) => [silobag.id, silobag]));

  return selectedIds.flatMap((id) => {
    const silobag = silobagById.get(id);
    return silobag ? [silobag] : [];
  });
}

function getRemainingStock(silobags: readonly SilobagListItem[]): number {
  return silobags.reduce((total, silobag) => {
    const stock = Number.parseFloat(silobag.remaining_stock);
    return total + (Number.isFinite(stock) ? stock : 0);
  }, 0);
}

function toLegacySelectedSilobags(silobags: readonly SilobagListItem[]): LegacySelectedSilobags {
  return silobags.map(({ id, name, active, write_permission, full_permission, remaining_stock }) => ({
    id,
    name,
    active,
    write_permission,
    full_permission,
    remaining_stock,
  })) as unknown as LegacySelectedSilobags;
}

type DataStateModalProps = Pick<Props, 'open' | 'onClose'> & {
  isLoading: boolean;
  canRetry?: boolean;
  invalidSelection?: boolean;
  onRetry?: () => void;
};

function ExtractionDataStateModal({
  open,
  onClose,
  isLoading,
  canRetry = false,
  invalidSelection = false,
  onRetry,
}: DataStateModalProps) {
  const i18n = HOOKS.useLocale();

  return (
    <UI.Modal
      open={open}
      onClose={onClose}
      title={i18n.t('temp.private.list.common.new_extraction')}
      actions={
        <>
          <UI.Button variant="alternative-hollow" uppercase={false} onClick={onClose}>
            {i18n.t('temp.common.cancel')}
          </UI.Button>
          {canRetry ? (
            <UI.Button variant="primary" uppercase={false} onClick={onRetry}>
              Reintentar
            </UI.Button>
          ) : null}
        </>
      }
      size="sm"
      fullWidth
      disableBackdropClose={isLoading}
      tid="silobag-extraction-data-modal"
    >
      {isLoading ? (
        <Stack alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Alert severity="error">
          {invalidSelection
            ? 'Seleccioná una o dos silobolsas para realizar la extracción.'
            : 'No se pudieron obtener los datos de las silobolsas seleccionadas.'}
        </Alert>
      )}
    </UI.Modal>
  );
}

function ReadyExtractionModal({
  open,
  silobags,
  onClose,
}: Omit<Props, 'silobagIds'> & {
  silobags: SilobagListItem[];
}) {
  const queryClient = useQueryClient();
  const [remainingStock, setRemainingStock] = useState<string | number | null>(() =>
    getRemainingStock(silobags)
  );
  const legacySilobags = toLegacySelectedSilobags(silobags);

  const i18n = HOOKS.useLocale();

  const handleExtractionSuccess = (success: boolean): void => {
    if (!success) return;

    toast.success(`${i18n.t('temp.common.toasts.success.new_extraction')}.`);
    void queryClient.invalidateQueries({ queryKey: SILOBAG_LIST_QUERY_KEY });
  };

  return (
    <Suspense fallback={<ExtractionDataStateModal open={open} onClose={onClose} isLoading />}>
      <LegacyExtractionAction
        type="new"
        open={open}
        setOpen={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        selectedSilobags={legacySilobags}
        remainingStock={remainingStock}
        setRemainingStock={setRemainingStock}
        writePermission={silobags.every((silobag) => silobag.write_permission)}
        active={silobags.every((silobag) => silobag.active)}
        fromSilobag
        lockSilobagSelection
        setExtractionSuccess={handleExtractionSuccess}
      />
    </Suspense>
  );
}

export function SilobagExtractionModal({ open, silobagIds, onClose }: Props) {
  const exactIdsKey = silobagIds.join('\u0000');
  const normalizedIds = useMemo(() => normalizeIds(exactIdsKey.split('\u0000')), [exactIdsKey]);
  const hasValidSelection = normalizedIds.length > 0 && normalizedIds.length <= MAX_EXTRACTION_SILOBAGS;
  const silobagsQuery = useQuery({
    queryKey: ['silobag-extraction-data', normalizedIds],
    queryFn: ({ signal }) =>
      listSilobagsService({
        exactIds: normalizedIds,
        pagination: { page: 0, take: normalizedIds.length },
        signal,
      }),
    enabled: open && hasValidSelection,
  });
  const selectedSilobags = useMemo(
    () => orderSilobagsBySelection(normalizedIds, silobagsQuery.data?.data ?? []),
    [normalizedIds, silobagsQuery.data?.data]
  );
  const isLoading = hasValidSelection && silobagsQuery.isPending;
  const hasDataError =
    !hasValidSelection ||
    silobagsQuery.isError ||
    (silobagsQuery.isSuccess && selectedSilobags.length !== normalizedIds.length);

  if (!isLoading && !hasDataError) {
    return <ReadyExtractionModal open={open} silobags={selectedSilobags} onClose={onClose} />;
  }

  return (
    <ExtractionDataStateModal
      open={open}
      onClose={onClose}
      isLoading={isLoading}
      canRetry={hasDataError && hasValidSelection}
      invalidSelection={!hasValidSelection}
      onRetry={() => void silobagsQuery.refetch()}
    />
  );
}
